import { Box } from "@mui/material";
import { LifestyleProduct } from "CommonTypes";
import { AddressForm, schemaShape as addressFormSchemaShape } from "components/AddressForm";
import { useAppErrors } from "components/AppErrors";
import { ContactForm, schemaShape as contactFormSchemaShape } from "components/ContactForm";
import InfoCard from "components/InfoCard";
import { PersonalDetailsForm, schemaShape as personalDetailsFormSchemaShape } from "components/PersonalDetailsForm";
import WizardSkeleton from "components/Skeletons";
import StyledStructuredText from "components/StyledStructuredText";
import { Responsive } from "components/ThemeWrapper";
import NavigationButtons from "components/Wizard/NavigationButtons";
import { format, parseISO } from "date-fns";
import { Formik } from "formik";
import React, { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDatoQuerySubscription } from "services/DatoCMS";
import { PrivacyQuery, PrivacyQueryResponse } from "services/DatoCMS/queries/PrivacyQuery";
import useStore, { PersonalDetailsState } from "state";
import { acceptsNationalAddresses } from "utils";
import * as yup from "yup";
import { InferType } from "yup";

export const buildPersonalDetailsSchema = (nationalAddresses: boolean) =>
    yup
        .object({})
        .shape(personalDetailsFormSchemaShape)
        .shape(addressFormSchemaShape(nationalAddresses))
        .shape(contactFormSchemaShape);

type Values = InferType<ReturnType<typeof buildPersonalDetailsSchema>>;

export const mapStateToForm = (state: PersonalDetailsState, isLifestyle: boolean): Values => ({
    personalDetailsForm: {
        title: state.title,
        givenNames: state.givenNames,
        isLifestyle,
        surname: state.surname,
        gender: state.gender,
        dateOfBirth: {
            day: state.dateOfBirth ? format(state.dateOfBirth, "dd") : "",
            month: state.dateOfBirth ? format(state.dateOfBirth, "MM") : "",
            year: state.dateOfBirth ? format(state.dateOfBirth, "yyyy") : "",
        },
    },
    addressForm: {
        residentialAddress: state.residentialAddress ? { ...state.residentialAddress } : null!,
        postalAddress: state.postalAddress ? { ...state.postalAddress } : null!,
        postalSameAsResidential: state.postalSameAsResidential,
    },
    contactForm: {
        emailAddress: state.emailAddress,
        preferredPhone: state.preferredPhone,
    },
});

export const mapFormToState = (values: Values): PersonalDetailsState => ({
    title: values.personalDetailsForm.title,
    givenNames: values.personalDetailsForm.givenNames,
    surname: values.personalDetailsForm.surname,
    gender: values.personalDetailsForm.gender,
    dateOfBirth: parseISO(
        `${values.personalDetailsForm.dateOfBirth.year}-${values.personalDetailsForm.dateOfBirth.month!.padStart(
            2,
            "0",
        )}-${values.personalDetailsForm.dateOfBirth.day!.padStart(2, "0")}`,
    ),
    residentialAddress: {
        global_address_key: values.addressForm.residentialAddress.global_address_key,
        text: values.addressForm.residentialAddress.text,
        format: values.addressForm.residentialAddress.format,
    },
    emailAddress: values.contactForm.emailAddress,
    preferredPhone: values.contactForm.preferredPhone,
    postalAddress: values.addressForm.postalAddress
        ? {
              global_address_key: values.addressForm.postalAddress.global_address_key,
              text: values.addressForm.postalAddress.text,
              format: values.addressForm.postalAddress.format,
          }
        : undefined,
    postalSameAsResidential: values.addressForm.postalSameAsResidential,
});

const Heading: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: 4,
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    maxWidth: "475px",
                    typography: "h2",
                    color: "primary.main",
                    fontWeight: 500,
                    fontSize: Responsive(20, 32),
                    lineHeight: Responsive("30px", "40px"),
                }}
            >
                {children}
            </Box>
        </Box>
    );
};

const Form: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: 6,
            }}
        >
            {children}
        </Box>
    );
};

export const PersonalDetails: React.FC<{ isLifestyle: boolean }> = ({ isLifestyle }) => {
    const navigate = useNavigate();
    const { raiseError } = useAppErrors();
    const { coverSelection, personalDetails, setPersonalDetails, titles, pricing } = useStore();
    const nationalAddresses = acceptsNationalAddresses(pricing.result);
    const validationSchema = useMemo(() => buildPersonalDetailsSchema(nationalAddresses), [nationalAddresses, isLifestyle]);
    const initialState = useMemo(() => mapStateToForm(personalDetails, isLifestyle), [personalDetails, isLifestyle]);

    const { data } = useDatoQuerySubscription<PrivacyQueryResponse>(PrivacyQuery, true);

    const onSubmit = useCallback(
        (values: Values) => {
            setPersonalDetails(mapFormToState(values));
            navigate("../confirm-details");
        },
        [navigate, setPersonalDetails],
    );

    useEffect(() => {
        if (!titles.result && !titles.loading) {
            titles.fetch().catch(raiseError);
        }
    }, [titles, raiseError]);

    return data?.roadside ? (
        <Formik initialValues={initialState} onSubmit={onSubmit} validationSchema={validationSchema}>
            {({ handleSubmit }) => (
                <form onSubmit={handleSubmit} autoComplete={"off"}>
                    <Box sx={{ pl: Responsive(0, 14), pr: Responsive(0, 14) }}>
                        <Heading>Your details</Heading>
                        <Form>
                            <PersonalDetailsForm titles={titles.result} />
                        </Form>
                        <Heading>Where you live</Heading>
                        <Form>
                            <AddressForm nationalAddresses={nationalAddresses} />
                        </Form>
                        <Heading>How we contact you</Heading>
                        <Form>
                            <ContactForm />
                        </Form>
                        <InfoCard variant={"info"}>
                            <StyledStructuredText data={data.roadside?.personalDetailsAlertBoxInfo} />
                        </InfoCard>
                        <NavigationButtons
                            previousLink={
                                coverSelection.product === LifestyleProduct.lifestyle
                                    ? "../information"
                                    : "../cover-selection"
                            }
                        />
                    </Box>
                </form>
            )}
        </Formik>
    ) : (
        <WizardSkeleton />
    );
};
